import { createRouter } from '@tanstack/react-router';
import { DelegatingPending } from './components/layouts/delegating-pending';
import { adminRoutes } from './routes/admin';
import {
  authenticatedLayout,
  indexRoute,
  onboardRoute,
  onboardStoreRoute,
} from './routes/authenticated';
import { orderRoutes } from './routes/orders';
import { receivingRoutes } from './routes/receiving';
import { reportRoutes } from './routes/reports';
import {
  previewRoutes,
  type RouterContext,
  rootRoute,
  signupRoute,
  trialLinkRoute,
} from './routes/root';
import {
  aiAssistantRoute,
  dashboardRoute,
  productsRoute,
  storeEditRoute,
  storeLayout,
  uploadsRoute,
  vendorEditRoute,
  vendorsRoute,
} from './routes/store';

// Route definitions live in routes/, one module per area. A module imports only
// its parent route, and assembles its own subtree when it owns the whole of it;
// the layouts other modules hang children off are assembled here. Pages and
// hooks read routes through route-apis.ts (getRouteApi), never by importing
// routes/, which would close an import cycle through the page modules.

// ---------------------------------------------------------------------------
// Route tree
// ---------------------------------------------------------------------------

export const routeTree = rootRoute.addChildren([
  ...previewRoutes,
  signupRoute,
  trialLinkRoute,
  authenticatedLayout.addChildren([
    indexRoute,
    onboardRoute,
    onboardStoreRoute,
    storeLayout.addChildren([
      dashboardRoute,
      storeEditRoute,
      aiAssistantRoute,
      vendorsRoute,
      vendorEditRoute,
      productsRoute,
      orderRoutes,
      uploadsRoute,
      reportRoutes,
      ...receivingRoutes,
    ]),
    adminRoutes,
  ]),
]);

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: DelegatingPending,
  defaultPendingMs: 0,
  context: {} as RouterContext,
});

// ---------------------------------------------------------------------------
// Register the router for type safety
// ---------------------------------------------------------------------------

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

declare module '@tanstack/history' {
  interface HistoryState {
    justSubmitted?: boolean;
  }
}
